import { Injectable } from '@angular/core';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { CompanyEmployeeData, EmployeeData } from 'src/lib/fliko/models/company/employee.data';
import { hasNonOfferPermissions } from 'src/lib/fliko/models/defaults/employeeRole.defaults';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { OfferFeature } from 'src/lib/fliko/models/offer.data';
import { CRMAdminAccessData } from 'src/lib/fliko/models/users/access.data';
import { getCompanyStartPath } from 'src/app/shared/helpers/employee-permissions.helper';
import { DatabaseService } from 'src/app/shared/services/database.service';
import { DirectDbAccessService } from 'src/app/shared/services/direct-database-access.service';
import { FireDbService } from 'src/app/shared/services/fireDb.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { ContextService } from './context.service';
import { ContextType } from './models/context.data';

export interface CrmStartContext {
  type: ContextType;
  companyId: string;
  estateId?: string;
  path: string;
}

export interface UserModuleAccess {
  hasCrmAccess: boolean;
  offersCompanyId: string | null;
  crmContext?: CrmStartContext;
}

interface CompanyAccessData {
  employee: CompanyEmployeeData | null;
  shardDbRef: ReturnType<typeof FireDbService.getDbRef>;
}

type CompanyAccessReader = (companyId: string) => Promise<CompanyAccessData | null>;

@Injectable({ providedIn: 'root' })
export class UserStartRouteService {
  constructor(
    private dbSrv: DatabaseService,
    private ctxSrv: ContextService,
    private localStorageSrv: LocalStorageService
  ) {}

  public async resolveLoginUrl(userId: string, returnUrl: string | null): Promise<string> {
    const access = await this.getModuleAccess(userId);
    if (access.hasCrmAccess) {
      const startUrl = this.activateCrmContext(userId, access.crmContext);
      return returnUrl ?? startUrl;
    }

    if (access.offersCompanyId) {
      this.activateOffersContext(userId, access.offersCompanyId);
      return '/offers';
    }

    return '/signIn';
  }

  public async ensureOffersContext(userId: string): Promise<boolean> {
    const access = await this.getModuleAccess(userId);
    if (!access.offersCompanyId) return false;

    this.activateOffersContext(userId, access.offersCompanyId);
    return true;
  }

  public async getModuleAccess(userId: string, initializeContext = true): Promise<UserModuleAccess> {
    if (initializeContext) this.ctxSrv.setUser(userId);
    return this.readModuleAccess(userId, true);
  }

  public getCrmModuleAccess(userId: string): Promise<UserModuleAccess> {
    return this.readModuleAccess(userId, false);
  }

  private async readModuleAccess(userId: string, includeOffers: boolean): Promise<UserModuleAccess> {
    const [crmAccess, employee, superAdminIds] = await Promise.all([
      this.readCommon<CRMAdminAccessData>(`crmAdminAccess/${userId}`),
      this.readCommon<EmployeeData>(`employee/${userId}`),
      this.readCommonList<string>('superadmin')
    ]);
    const companyReads = new Map<string, Promise<CompanyAccessData | null>>();
    const readCompany: CompanyAccessReader = (companyId) => {
      let request = companyReads.get(companyId);
      if (!request) {
        request = this.readCompanyAccess(userId, companyId);
        companyReads.set(companyId, request);
      }
      return request;
    };

    const isSuperAdmin = superAdminIds.includes(userId);
    const crmContextRequest = isSuperAdmin ? Promise.resolve(null) : this.getCrmContext(userId, crmAccess, employee, readCompany);
    const offersCompanyRequest = includeOffers
      ? this.getOffersCompanyId(userId, crmAccess, employee, readCompany)
      : crmContextRequest.then((context) =>
          isSuperAdmin || context != null ? null : this.getOffersCompanyId(userId, crmAccess, employee, readCompany)
        );
    const [crmContext, offersCompanyId] = await Promise.all([crmContextRequest, offersCompanyRequest]);
    return {
      hasCrmAccess: isSuperAdmin || crmContext != null,
      offersCompanyId,
      ...(crmContext ? { crmContext } : {})
    };
  }

  public activateOffersContext(userId: string, companyId: string): void {
    this.ctxSrv.setUser(userId);
    this.ctxSrv.setCompany(companyId);
    this.ctxSrv.setContext(ContextType.COMPANY);
  }

  public activateCrmContext(userId: string, context?: CrmStartContext): string {
    if (!context) return this.getCrmStartUrl(userId);
    this.ctxSrv.setCompany(context.companyId);
    if (context.estateId) this.ctxSrv.setEstate(context.estateId);
    this.ctxSrv.setContext(context.type);
    return context.path;
  }

  public getCrmStartUrl(userId: string): string {
    const context = this.localStorageSrv.userContext.get(userId);
    return context === ContextType.COMPANY ? '/company' : '/dashboard';
  }

  private async getOffersCompanyId(
    userId: string,
    crmAccess: CRMAdminAccessData | null,
    employee: EmployeeData | null,
    readCompany: CompanyAccessReader
  ): Promise<string | null> {
    const companyIds = new Set<string>();
    ObjHlp.keys(employee?.companies).forEach((companyId) => {
      const id = companyId.toString();
      if (employee?.companies?.[id]) companyIds.add(id);
    });
    ObjHlp.keys(crmAccess?.companies).forEach((companyId) => {
      const id = companyId.toString();
      if (crmAccess?.companies?.[id]) companyIds.add(id);
    });

    const preferredCompanyId = this.localStorageSrv.selectedCompany.get(userId);
    const orderedCompanyIds = [
      ...(preferredCompanyId && companyIds.has(preferredCompanyId) ? [preferredCompanyId] : []),
      ...Array.from(companyIds).filter((companyId) => companyId !== preferredCompanyId)
    ];

    for (const companyId of orderedCompanyIds) {
      if (await this.hasOffersAccess(companyId, readCompany)) return companyId;
    }
    return null;
  }

  private async hasOffersAccess(companyId: string, readCompany: CompanyAccessReader): Promise<boolean> {
    const company = await readCompany(companyId);
    if (!company?.employee?.permissions?.offers?.view) return false;

    const offerFeature = await DirectDbAccessService.onceObject<OfferFeature>(
      `companyFeature/${companyId}/both/offer`,
      company.shardDbRef
    );
    return offerFeature?.enabled === true;
  }

  private async readCompanyAccess(userId: string, companyId: string): Promise<CompanyAccessData | null> {
    const shardName = await this.dbSrv.getDbNameFromId(companyId);
    const shardDbRef = shardName ? FireDbService.getDbRef(shardName) : null;
    if (!shardDbRef) return null;

    const employee = await DirectDbAccessService.onceObject<CompanyEmployeeData>(
      `companyEmployee/${companyId}/${userId}`,
      shardDbRef
    );
    return { employee, shardDbRef };
  }

  private async getCrmContext(
    userId: string,
    crmAccess: CRMAdminAccessData | null,
    employeeData: EmployeeData | null,
    readCompany: CompanyAccessReader
  ): Promise<CrmStartContext | null> {
    const companyIds = new Set<string>([
      ...Object.entries(employeeData?.companies ?? {})
        .filter(([, allowed]) => allowed === true)
        .map(([id]) => id),
      ...Object.entries(crmAccess?.companies ?? {})
        .filter(([, allowed]) => allowed === true)
        .map(([id]) => id)
    ]);
    const preferredCompanyId = this.localStorageSrv.selectedCompany.get(userId);
    const preferredContext = this.localStorageSrv.userContext.get(userId);
    const estateIds = Object.entries(crmAccess?.estates ?? {})
      .filter(([, allowed]) => allowed === true)
      .map(([id]) => id);

    if (
      estateIds.length === 0 ||
      (preferredContext !== ContextType.ESTATE && (!preferredCompanyId || companyIds.has(preferredCompanyId)))
    ) {
      const directCompanyIds = [
        ...(preferredCompanyId && companyIds.has(preferredCompanyId) ? [preferredCompanyId] : []),
        ...Array.from(companyIds).filter((companyId) => companyId !== preferredCompanyId)
      ];
      for (const companyId of directCompanyIds) {
        const company = await readCompany(companyId);
        if (!hasNonOfferPermissions(company?.employee?.permissions)) continue;
        const companyPath = getCompanyStartPath(company?.employee?.permissions ?? {});
        if (companyPath) return { type: ContextType.COMPANY, companyId, path: companyPath };
        // Estate permissions in an earlier company retain their selection priority.
        break;
      }
    }

    const estates = (await Promise.all(estateIds.map((estateId) => this.readCommon<EstateData>(`estate/${estateId}`)))).filter(
      (estate): estate is EstateData => estate != null && !estate.hidden
    );
    estates.forEach((estate) => {
      if (estate?.companyId) companyIds.add(estate.companyId);
    });
    const orderedCompanyIds = [
      ...(preferredCompanyId && companyIds.has(preferredCompanyId) ? [preferredCompanyId] : []),
      ...Array.from(companyIds).filter((companyId) => companyId !== preferredCompanyId)
    ];
    for (const companyId of orderedCompanyIds) {
      const company = await readCompany(companyId);
      const employee = company?.employee;
      if (!hasNonOfferPermissions(employee?.permissions)) continue;
      const companyPath = getCompanyStartPath(employee?.permissions ?? {});
      const companyEstates = estates.filter((estate): estate is EstateData => estate?.companyId === companyId);
      const preferredEstateId = this.localStorageSrv.selectedEstate.get(userId);
      const estate = companyEstates.find((entry) => entry.id === preferredEstateId) ?? companyEstates[0];
      if (estate && (preferredContext === ContextType.ESTATE || !companyPath)) {
        return { type: ContextType.ESTATE, companyId, estateId: estate.id, path: '/dashboard' };
      }
      if (companyPath) return { type: ContextType.COMPANY, companyId, path: companyPath };
    }
    return null;
  }

  private async readCommon<T>(path: string): Promise<T | null> {
    try {
      return (await this.dbSrv.commonFactory.getObject<T>(path).first()) ?? null;
    } catch {
      return null;
    }
  }

  private async readCommonList<T>(path: string): Promise<T[]> {
    try {
      return await this.dbSrv.commonFactory.getCollection<T>(path).onceList();
    } catch {
      return [];
    }
  }
}
